import * as React from "react";
import { format } from "date-fns";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { Calendar } from "@/modern/components/ui/calendar";
import { Label } from "@/modern/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/modern/components/ui/popover";
import type { Matcher } from "react-day-picker";

export interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  disabledDays?: Matcher | Matcher[];
  clearable?: boolean;
  className?: string;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  label,
  error,
  disabled = false,
  disabledDays,
  clearable = false,
  className
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {label && <Label className="mb-swivu-vertical">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="input"
            className={cn(
              "w-full justify-start text-left font-normal",
              !value && "text-muted-foreground",
              error && "border-destructive",
              "relative"
            )}
            disabled={disabled}
          >
            <i className="fa-light fa-calendar" />
            {value ? format(value, "PPP") : <span>{placeholder}</span>}
            {clearable && value && (
              <div className="absolute right-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex items-center justify-center hover:bg-destructive/40"
                  onClick={handleClear}
                  disabled={disabled}
                >
                  <i className="fa-light fa-xmark" />
                  <span className="sr-only">Clear date</span>
                </Button>
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[--swivu-bryntum-z-index]" align="start">
          <Calendar
            mode="single"
            selected={value || undefined}
            onSelect={handleSelect}
            disabled={disabledDays}
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-swivu-sm text-destructive mt-2">{error}</p>}
    </div>
  );
}
